import { useState, useRef } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import Turnstile, { TURNSTILE_ENABLED } from "./Turnstile";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "./ui/dialog";

const EMPTY = { name: "", email: "", linkedin: "", investor_type: "", referred_by: "", message: "" };

export default function ApplyMembershipModal({ trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const tsRef = useRef(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const needsToken = TURNSTILE_ENABLED;

  const change = (o) => {
    setOpen(o);
    if (o) { setSent(false); setF(EMPTY); setToken(""); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/apply-membership", { ...f, turnstile_token: token });
      setSent(true);
    } catch (e) {
      toast.error(apiError(e));
      setToken("");
      tsRef.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="!bg-[#14171A] !border-[#24272B] !rounded-none !max-w-lg p-0 text-[#E8E6E1] max-h-[90vh] overflow-y-auto"
        data-testid="apply-membership-modal">
        <DialogTitle className="sr-only">Apply for membership</DialogTitle>
        <DialogDescription className="sr-only">Apply to Solera membership. Name, email and LinkedIn profile are required.</DialogDescription>
        <div className="p-8">
          {sent ? (
            <div data-testid="apply-success">
              <span className="t-label">Received</span>
              <p className="t-body text-bone mt-3 measure">
                Thank you. Your application has reached us and every applicant is reviewed by hand. If there is a
                fit, a member of the team will be in touch directly. We have sent a confirmation to your email.
              </p>
              <button className="btn-ghost mt-6" onClick={() => change(false)} data-testid="apply-close">Close</button>
            </div>
          ) : (
            <div>
              <span className="t-label">Apply for membership</span>
              <p className="t-small text-muted-s mt-3 measure">
                Membership is by referral and every applicant is reviewed by hand. Tell us who you are and why
                there is a fit. Nothing about you is published anywhere, at any point.
              </p>
              <div className="space-y-3 mt-6">
                <input className="input-solera" placeholder="Full name" value={f.name} onChange={set("name")} data-testid="apply-name-input" />
                <input className="input-solera" type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="apply-email-input" />
                <input className="input-solera" placeholder="LinkedIn profile URL" value={f.linkedin} onChange={set("linkedin")} data-testid="apply-linkedin-input" />
                <select className="input-solera" value={f.investor_type} onChange={set("investor_type")} data-testid="apply-type-select">
                  <option value="">Investor type (optional)</option>
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                  <option value="both">Both</option>
                </select>
                <input className="input-solera" placeholder="Referred by (optional)" value={f.referred_by} onChange={set("referred_by")} data-testid="apply-referrer-input" />
                <textarea className="input-solera" rows={3} placeholder="Why there is a fit (optional)" value={f.message} onChange={set("message")} data-testid="apply-message-input" />
                <Turnstile ref={tsRef} onToken={setToken} />
                <button
                  className="btn-primary w-full"
                  disabled={busy || !f.name || !f.email || !f.linkedin || (needsToken && !token)}
                  onClick={submit}
                  data-testid="apply-submit-button">
                  {busy ? "Sending" : "Submit application"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
